import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import gsap from 'gsap';
const AIBot = ({ className = '' }) => {
    const botRef = useRef(null);
    const containerRef = useRef(null);
    const leftPupilRef = useRef(null);
    const rightPupilRef = useRef(null);
    const rightArmRef = useRef(null);
    const leftArmRef = useRef(null);
    const bannerRef = useRef(null);
    useEffect(() => {
        if (!botRef.current || !containerRef.current)
            return;
        // Floating animation
        gsap.to(botRef.current, {
            y: 15,
            duration: 2.5,
            yoyo: true,
            repeat: -1,
            ease: 'sine.inOut'
        });
        // Subtle rotation
        gsap.to(botRef.current, {
            rotationZ: 5,
            duration: 3,
            yoyo: true,
            repeat: -1,
            ease: 'sine.inOut'
        });
        // Animation Sequence: Wave (Right) -> Hold Banner (Left) -> Loop
        if (rightArmRef.current && leftArmRef.current && bannerRef.current) {
            gsap.set(rightArmRef.current, { transformOrigin: "50% 5%" });
            gsap.set(leftArmRef.current, { transformOrigin: "50% 5%" });
            gsap.set(bannerRef.current, { opacity: 0, scale: 0 });
            const masterTimeline = gsap.timeline({ repeat: -1, repeatDelay: 1 });
            // 1. Wave (Right Arm)
            masterTimeline
                .to(rightArmRef.current, {
                rotation: -140,
                duration: 0.6,
                ease: "back.out(1.2)"
            })
                .to(rightArmRef.current, {
                rotation: -100,
                duration: 0.25,
                yoyo: true,
                repeat: 3,
                ease: "sine.inOut"
            })
                .to(rightArmRef.current, {
                rotation: 0,
                duration: 0.5,
                ease: "power2.inOut"
            })
                // 2. Move Left Arm to holding position
                .to(leftArmRef.current, {
                rotation: 90,
                duration: 0.4,
                ease: "power2.out"
            })
                // 3. Show Banner (on left)
                .to(bannerRef.current, {
                opacity: 1,
                scale: 1,
                duration: 0.4,
                ease: "back.out(1.7)"
            }, "<+=0.2") // Start slightly after arm moves
                // 4. Hold for 5 seconds
                .to({}, { duration: 5 })
                // 5. Hide Banner
                .to(bannerRef.current, {
                opacity: 0,
                scale: 0,
                duration: 0.3,
                ease: "power2.in"
            })
                // 6. Left Arm down
                .to(leftArmRef.current, {
                rotation: 0,
                duration: 0.5,
                ease: "power2.inOut"
            });
        }
        // Eye Tracking
        const handleMouseMove = (e) => {
            [leftPupilRef, rightPupilRef].forEach(ref => {
                if (!ref.current)
                    return;
                const pupil = ref.current;
                const eye = pupil.parentElement;
                if (!eye)
                    return;
                const rect = eye.getBoundingClientRect();
                const eyeCenterX = rect.left + rect.width / 2;
                const eyeCenterY = rect.top + rect.height / 2;
                const angle = Math.atan2(e.clientY - eyeCenterY, e.clientX - eyeCenterX);
                const maxMove = (rect.width - pupil.offsetWidth) / 2;
                const distToCursor = Math.hypot(e.clientX - eyeCenterX, e.clientY - eyeCenterY);
                const moveDist = Math.min(maxMove, distToCursor / 15); // Reduced sensitivity
                const x = Math.cos(angle) * moveDist;
                const y = Math.sin(angle) * moveDist;
                gsap.to(pupil, { x, y, duration: 0.1, overwrite: 'auto' });
            });
        };
        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);
    return (_jsx("div", { ref: containerRef, className: `flex items-center justify-center ${className}`, style: { transition: 'all 0.3s ease' }, children: _jsx("div", { ref: botRef, className: "w-full h-full flex items-center justify-center scale-125", children: _jsxs("div", { className: "relative w-20 h-28 md:w-24 md:h-32", children: [_jsxs("div", { className: "absolute top-0 left-1/2 transform -translate-x-1/2 w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-cyan-300 to-blue-500 rounded-2xl shadow-lg border-2 border-cyan-200", children: [_jsx("div", { className: "absolute inset-0 bg-gradient-to-br from-white/30 to-transparent rounded-2xl" }), _jsx("div", { className: "absolute top-5 left-4 md:left-5 w-2 h-2 md:w-3 md:h-3 bg-white rounded-full flex items-center justify-center", children: _jsx("div", { ref: leftPupilRef, className: "bg-gray-800 rounded-full w-1 h-1 md:w-1.5 md:h-1.5" }) }), _jsx("div", { className: "absolute top-5 right-4 md:right-5 w-2 h-2 md:w-3 md:h-3 bg-white rounded-full flex items-center justify-center", children: _jsx("div", { ref: rightPupilRef, className: "bg-gray-800 rounded-full w-1 h-1 md:w-1.5 md:h-1.5" }) }), _jsx("div", { className: "absolute bottom-3 left-1/2 transform -translate-x-1/2 w-3 h-0.5 md:w-4 md:h-1 bg-white rounded-full" })] }), _jsx("div", { className: "absolute top-16 md:top-20 left-1/2 transform -translate-x-1/2 w-3 h-2 bg-gradient-to-b from-cyan-300 to-blue-400 md:w-4 md:h-3" }), _jsxs("div", { className: "absolute top-20 md:top-24 left-1/2 transform -translate-x-1/2 w-12 h-6 md:w-16 md:h-8 bg-gradient-to-br from-violet-400 to-blue-600 rounded-xl shadow-md border border-violet-300", children: [_jsx("div", { className: "absolute inset-1 border border-cyan-300/30 rounded-lg" }), _jsx("div", { ref: leftArmRef, className: "absolute -left-2 top-1 w-3 h-6 bg-gradient-to-r from-violet-400 to-violet-500 rounded-full md:w-4 md:h-8 shadow-sm z-50 origin-top-center", style: { transformBox: 'fill-box' } }), _jsx("div", { ref: rightArmRef, className: "absolute -right-2 top-1 w-3 h-6 bg-gradient-to-r from-violet-400 to-violet-500 rounded-full md:w-4 md:h-8 shadow-sm z-50 origin-top-center", style: { transformBox: 'fill-box' } })] }), _jsxs("div", { ref: bannerRef, className: "absolute -top-16 left-1/2 -translate-x-1/2 md:top-12 md:-left-40 md:left-auto md:translate-x-0 bg-white text-violet-600 px-4 py-2 rounded-xl shadow-xl border-2 border-violet-200 font-bold text-base whitespace-nowrap z-50 origin-bottom md:origin-right", children: ["AI Help", _jsx("div", { className: "absolute -bottom-1.5 left-1/2 -translate-x-1/2 border-b-2 border-r-2 border-t-0 border-l-0 md:top-1/2 md:-right-2 md:bottom-auto md:left-auto md:translate-x-0 md:-translate-y-1/2 md:border-t-2 md:border-r-2 md:border-b-0 w-3 h-3 bg-white border-violet-200 transform rotate-45" })] }), _jsx("div", { className: "absolute -top-3 left-2 w-1 h-4 md:w-1.5 md:h-5 bg-gradient-to-t from-cyan-300 to-cyan-200 rounded-full" }), _jsx("div", { className: "absolute -top-3 right-2 w-1 h-4 md:w-1.5 md:h-5 bg-gradient-to-t from-cyan-300 to-cyan-200 rounded-full" }), _jsx("div", { className: "absolute -top-1 left-2 w-1.5 h-1.5 md:w-2 md:h-2 bg-cyan-200 rounded-full shadow-lg" }), _jsx("div", { className: "absolute -top-1 right-2 w-1.5 h-1.5 md:w-2 md:h-2 bg-cyan-200 rounded-full shadow-lg" })] }) }) }));
};
export default AIBot;
